/**
 * Tower shooting domain: target selection, damage computation (plan 07).
 * Pure helpers and constants; no I/O. Mutating ops (damageMonster, removeMonster, tickTowerShooting) live in session.ts.
 */

import type { Cell } from "./types.js";
import type { GameSession } from "./session.js";
import type { GemType } from "./types.js";
import type { Monster, Tower } from "./entities.js";
import { GEM_SPECS, DEFAULT_FIRE_RATE_MS } from "./gem-specs.js";
import { nextRandom } from "./gem-effects.js";
import { TRAP_RANGE_CELLS, TRAP_DAMAGE_MULT, TRAP_FIRE_RATE_MULT } from "./session.js";

export const AMPLIFIER_RADIUS = 2; // Chebyshev cells
export const AMPLIFIER_FIRE_RATE_BONUS = 0.2; // +20% fire rate = 0.8× cooldown ms

/** Tower range in cells. Targeting adds half a cell to match the visual range preview around a tile. */
export const TOWER_RADIUS_CELLS = 3;
export const TOWER_TARGET_RADIUS_PADDING = 0.5;

/**
 * Roll a stochastic base damage for a single shot (AS3-faithful):
 *   dmg = round((damageMin + random * (damageMax - damageMin)) * level)
 * Uses the session's seeded RNG so fights are deterministic given the same seed.
 * For Trap towers, damage is multiplied by TRAP_DAMAGE_MULT (0.2 = -80%).
 */
export function rollBaseDamage(session: GameSession, gemType: GemType, level: number, tower?: Tower): number {
  const spec = GEM_SPECS[gemType];
  const roll = spec.damageMin + nextRandom(session) * (spec.damageMax - spec.damageMin);
  let scaled = roll * level * (1 + 0.01 * session.skillEffects.damage);
  if (tower?.type === "Trap") scaled *= TRAP_DAMAGE_MULT * (1 + 0.01 * session.skillEffects.traps);
  return Math.round(scaled);
}

/**
 * @deprecated Use rollBaseDamage(session, gemType, level) for AS3-faithful stochastic damage.
 * Returns the average damage (midpoint of min/max range) × level.
 * Kept for backward compatibility with tests that rely on a scalar result.
 */
export function getBaseDamage(gemType: GemType, level: number): number {
  const spec = GEM_SPECS[gemType];
  return ((spec.damageMin + spec.damageMax) / 2) * level;
}

/** Fire rate in ms for a tower's currently-equipped gem. Returns DEFAULT_FIRE_RATE_MS if no gem.
 *  If an Amplifier is within AMPLIFIER_RADIUS (Chebyshev), applies AMPLIFIER_FIRE_RATE_BONUS.
 */
export function getFireRateMs(session: GameSession, tower: Tower): number {
  const slot = tower.gemSlots.find((s) => s.gem !== null);
  if (!slot?.gem) return DEFAULT_FIRE_RATE_MS;
  // FIRING_SPEED skill: divide base fire rate by (1 + val/100)
  let rate = GEM_SPECS[slot.gem.type].fireRateMs / (1 + 0.01 * session.skillEffects.firingSpeed);
  // Upgrade bonus: each level above 1 provides a 5% fire rate boost (dividing period by 1.05)
  if (slot.gem.level > 1) {
    rate /= (1 + (slot.gem.level - 1) * 0.05);
  }
  rate *= slot.gem.mods?.fireRateMult ?? 1;
  if (tower.type === "Trap") rate *= TRAP_FIRE_RATE_MULT;
  const ampInRange = session.towers.some(
    (t) =>
      t.type === "Amplifier" &&
      chebyshevDistance(t.cell, tower.cell) <= AMPLIFIER_RADIUS
  );
  if (ampInRange) {
    const bonus = AMPLIFIER_FIRE_RATE_BONUS * (1 + 0.01 * session.skillEffects.amplifierPower);
    rate = rate * (1 - bonus);
  }
  return Math.round(rate);
}

/**
 * Tower range by gem type, with optional RANGE skill boost.
 * Trap towers use TRAP_RANGE_CELLS. Regular towers read per-gem rangeCells
 * from GEM_SPECS (AS3 shotData.range parity).
 */
export function getTowerRadius(gemType: GemType, session?: GameSession, tower?: Tower, level = 1): number {
  const base = tower?.type === "Trap" ? TRAP_RANGE_CELLS : GEM_SPECS[gemType].rangeCells;
  const levelMult = 1 + 0.05 * (level - 1);
  const skillMult = session && session.skillEffects.range > 0 ? (1 + 0.01 * session.skillEffects.range) : 1;
  return base * levelMult * skillMult;
}

/** Chebyshev distance between two positions: max(|Δrow|, |Δcol|). Accepts continuous coords. */
function chebyshevDistance(
  a: Cell | { row: number; col: number },
  b: Cell | { row: number; col: number }
): number {
  return Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col));
}

/** Euclidean distance between two positions. Accepts continuous coords. */
function euclideanDistance(
  a: Cell | { row: number; col: number },
  b: Cell | { row: number; col: number }
): number {
  const dr = a.row - b.row;
  const dc = a.col - b.col;
  return Math.hypot(dr, dc);
}

/** True if tower has at least one gem in slots. */
function towerHasGem(tower: Tower): boolean {
  return tower.gemSlots.some((s) => s.gem !== null);
}

/**
 * Target for tower: monster in radius closest to the altar (furthest along the BFS road).
 * Tie-break: smaller circular distance from monster position to tower (more centered in arc).
 */
export function getTowerTarget(
  session: GameSession,
  tower: Tower
): Monster | null {
  if (!towerHasGem(tower)) return null;
  const slot = tower.gemSlots.find((s) => s.gem !== null);
  const radius = getTowerRadius(slot?.gem?.type ?? "Yellow", session, tower, slot?.gem?.level ?? 1);
  const targetRadius = radius + TOWER_TARGET_RADIUS_PADDING;
  const inRadius = session.monsters.filter(
    (m) => euclideanDistance(tower.cell, m.position) <= targetRadius
  );
  if (inRadius.length === 0) return null;
  const altar = session.field.exitCell;
  const manhattanToAltar = (m: Monster): number =>
    Math.abs(m.currentCell.row - altar.row) + Math.abs(m.currentCell.col - altar.col);
  let best = inRadius[0];
  let bestD = manhattanToAltar(best);
  let bestDistance = euclideanDistance(tower.cell, best.position);
  for (let i = 1; i < inRadius.length; i++) {
    const m = inRadius[i];
    const d = manhattanToAltar(m);
    const distance = euclideanDistance(tower.cell, m.position);
    if (d < bestD || (d === bestD && distance < bestDistance)) {
      best = m;
      bestD = d;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Effective damage from tower to monster: max(0, rollBaseDamage - monster.armor).
 * Uses session RNG for stochastic roll (AS3-faithful).
 * Uses first slot with a gem.
 */
export function computeDamage(session: GameSession, tower: Tower, monster: Monster): number {
  const slot = tower.gemSlots.find((s) => s.gem !== null);
  if (!slot?.gem) return 0;
  const base = rollBaseDamage(session, slot.gem.type, slot.gem.level, tower);
  const dmgMult = slot.gem.mods?.dmgMult ?? 1;
  return Math.max(0, Math.round(base * dmgMult) - monster.armor);
}
